<template>
  <b-card
    no-body
    tag="article"
    class="mb-3 mr-3"
  >
    <h4 slot="header" class="mb-0">
      <i class="fas fa-exclamation-circle"></i>
      <span class="pl-2">{{rule.label}}</span>
      <div class="float-right">
        <label class="switch switch-sm switch-text switch-info mb-0">
          <input class="switch-input" type="checkbox" v-model="rule.enabled" @click.stop="toggleRuleState();">
          <span data-on="On" data-off="Off" class="switch-label"></span>
          <span class="switch-handle"></span>
        </label>
      </div>
    </h4>
    <b-card-body>
      <p class="card-text">
        <table class="table rule-table">
          <tbody>
            <tr>
              <th scope="row">Device</th>
              <td v-html="devices"></td>
            </tr>
            <tr>
              <th scope="row">Rule</th>
              <td>{{ getFormattedRule(rule) }}</td>
            </tr>
            <tr>
              <th scope="row">Aggregation</th>
              <td>{{ rule.aggPeriod == 0 ? 'Live (no aggregation)' : formatSeconds(rule.aggPeriod) + ' Minute Avg.' }}</td>
            </tr>
            <tr>
              <th scope="row">Reminder</th>
              <td>{{ reminderIntervals[rule.reminderInterval] }}</td>
            </tr>
            <tr>
              <th scope="row">Expires</th>
              <td>{{ expireIntervals[rule.expiresIn] }}</td>
            </tr>
            <tr>
              <th scope="row">Send To</th>
              <td v-html="targets"></td>
            </tr>
            <tr v-if="ruleActions.length">
              <th scope="row">Actions</th>
              <td>
                <div v-for="action in ruleActions" :key="action.id">
                  <i class="fa fa-bolt mr-1" :class="action.enabled === false ? 'text-muted' : 'text-warning'"></i>
                  <span :class="action.enabled === false ? 'text-muted' : ''">
                    {{ actionSummary(action) }}
                    <em v-if="action.enabled === false">(off)</em>
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <b-button @click.stop="$emit('edit-rule')" size="sm" variant="primary" class="mr-1"><i class="fas fa-edit"></i><span class="pl-2">Edit</span></b-button>
        <b-button @click.stop="deleteRule()" v-if="rule.id" size="sm" variant="danger" class="mr-1"><i class="fas fa-trash"></i><span class="pl-2">Delete</span></b-button>
      </p>
    </b-card-body>
  </b-card>
</template>

<script>
import moment from 'moment-timezone'
import { DELETE_RULE_MUTATION, UPDATE_RULE_MUTATION } from '@/constants/graphql'
import { DEVICE_METRICS, REMINDER_INTERVALS, EXPIRE_INTERVALS } from '@/constants/data'

export default {
  name: 'RuleCard',
  props: {
    rule: {
      type: Object,
      default () {
        return {
          id: null,
          enabled: true,
          label: null,
          method: null,
          destination: null
        }
      }
    }
  },
  data () {
    return {
      loading: 0,
      error: null,
      methodOptions: [
        { value: null, text: 'Please select a method' },
        { value: 'EMAIL', text: 'Email' },
        { value: 'SMS', text: 'Text Message' }
      ],
      deviceMetrics: DEVICE_METRICS,
      reminderIntervals: REMINDER_INTERVALS,
      expireIntervals: EXPIRE_INTERVALS
    }
  },
  computed: {
    ruleActions () {
      return this.rule.actions || []
    },
    devices () {
      return this.rule.devices.map(t => t.name).join("<br />")
    },
    targets () {
      if (this.rule.targets.length) {
        return this.rule.targets.map(t => t.label).join("<br />")
      }

      return "Dashboard Only"
    }
  },
  methods: {
    actionSummary (action) {
      if (action.type === 'setSleepType') {
        var mode = action.params && action.params.sleepType
        return 'Set sleep mode to ' + (mode === 'ulp' ? 'ULP (ultra low power)' : mode === 'stop' ? 'Stop / Network' : mode)
      }
      var params = action.params || {}
      var keys = Object.keys(params)
      return action.type + (keys.length ? ' (' + keys.map(function (k) { return k + ': ' + params[k] }).join(', ') + ')' : '')
    },
    getFormattedRule (rule) {
      let deviceType = (rule.devices.length) ? rule.devices[0].type : 'plunge'
      let properName = this.deviceMetrics[deviceType].filter((m) => m.value === rule.metricName)[0].text
      return `${properName} ${rule.comparator} ${rule.triggerValue}`
    },
    formatSeconds (numSeconds) {
      return moment.utc(numSeconds * 1000).format('m')
    },
    toggleRuleState () {
      this.rule.enabled = !this.rule.enabled
      let data = { enabled: this.rule.enabled }

      this.$apollo.mutate({
        mutation: UPDATE_RULE_MUTATION,
        variables: { data, id: this.rule.id }
      }).then(() => {
        let statusText = (this.rule.enabled) ? 'Enabled' : 'Disabled'
        this.$swal.fire({ icon: 'success', title: 'Success! Rule ' + statusText })
        this.$emit('saved')
      }).catch(() => {
        // handled by global onError handler but still need to catch in the promise
      })
    },
    deleteRule () {
      this.$swal.fire({
        title: `Delete this rule?`,
        icon: 'warning',
        toast: false,
        position: 'top',
        showConfirmButton: true,
        timer: 0,
        timerProgressBar: false,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: `Yes, delete it!`
      }).then((result) => {
        if (result.isConfirmed && this.rule.id) {
          this.$apollo.mutate({
            mutation: DELETE_RULE_MUTATION,
            variables: { id: this.rule.id }
          }).then(() => {
            this.loading = false
            this.$swal.fire({ icon: 'success', title: 'Success! Rule Deleted' })
            this.$emit('deleted')
          }).catch((error) => {
            this.loading = false
            this.$swal.fire({ icon: 'error', timer: 3500, title: 'Error: ' + error })
          })
        }
      })
    }
  }
}
</script>

<style>
table.rule-table tbody tr:first-child td, table.rule-table tbody tr:first-child th {
  border-top: none !important;
}
.card-header h4 {
    font-size: 1.2em;
    line-height: 1.2em;
}
article.card {
  width: 22rem;
  min-width: 20rem;
  max-width: 25rem;
}
article.card {
  background-color: rgba(255,255,255, 0.9);
}
</style>
